import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Input,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import classnames from "classnames";
import { requestJson } from "../../helpers/api";

type AgentWidgetProps = {
  isOpen: boolean;
  onToggle: () => void;
  informeKey: string;
  snapshotPayload?: {
    filtros: any;
    datos: any;
  };
  snapshotReady?: boolean;
  collectionPermissions?: {
    canView: boolean;
    canEdit: boolean;
    canClone: boolean;
  };
  onApplyFilters?: (payload: {
    fecha?: string | null;
    sucursal?: string | string[] | null;
    familia?: string | null;
    linea?: string | null;
    servidor?: string | null;
    differenceFilter?: string | null;
    controlMode?: string | null;
    metricMode?: string | null;
    dateMode?: string | null;
    week?: string | null;
    month?: string | null;
    rangeFrom?: string | null;
    rangeTo?: string | null;
    searchTerm?: string | null;
    semanticTokens?: string[] | null;
    collectionFilter?: string | null;
    inventoryMovementInitial?: string | null;
    inventoryMovementFinal?: string | null;
    groupViewMode?: string | null;
  }) => void;
  onApplyTab?: (tabId: string) => void;
  onApplyUiAction?: (action: {
    tipo: string;
    valor?: any;
    campo?: string;
    [key: string]: any;
  }) => Promise<void> | void;
};

type ChatSession = {
  id: number;
  titulo: string;
  informe_key: string;
  creado_en: string;
  actualizado_en: string;
  preview: string | null;
};

type ChatMessage = {
  id: number;
  rol: string;
  contenido: string;
  metadata?: any;
  creado_en: string;
};

type UiBlock =
  | {
      type: "notice";
      tone?: "info" | "success" | "warning" | "danger";
      text: string;
      source?: "agent" | "report" | "summary";
    }
  | {
      type: "kpis";
      items: Array<{ label: string; value: string; tone?: string }>;
      source?: "agent" | "report" | "summary";
    }
  | {
      type: "table";
      title?: string;
      columns: string[];
      rows: Array<Record<string, any>>;
      source?: "agent" | "report" | "summary";
    }
  | {
      type: "list";
      title?: string;
      items: string[];
      source?: "agent" | "report" | "summary";
    };

const AgentWidget = ({
  isOpen,
  onToggle,
  informeKey,
  snapshotPayload,
  onApplyFilters,
  onApplyTab,
  onApplyUiAction,
  snapshotReady = false,
  collectionPermissions,
}: AgentWidgetProps) => {
  const isAgentDebug = () =>
    typeof window !== "undefined" && (window as any).__AGENTE_DEBUG__ === true;
  const debugLog = (label: string, data?: any) => {
    if (!isAgentDebug()) return;
    // eslint-disable-next-line no-console
    console.debug(`[AgenteIA] ${label}`, data ?? "");
  };
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState<"chat" | "history">("chat");
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [uiBlocksByMessageId, setUiBlocksByMessageId] = useState<
    Record<number, UiBlock[]>
  >({});
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [processingState, setProcessingState] = useState<string | null>(null);
  const isAnalyzingRef = React.useRef(false);
  const greetedSessionRef = React.useRef<number | null>(null);
  const pendingUiBlocksRef = React.useRef<UiBlock[] | null>(null);
  const lastUserMessageIdRef = React.useRef<number | null>(null);
  const lastAnalyzedMessageIdRef = React.useRef<number | null>(null);

  const getUserName = () => {
    try {
      const authUser = sessionStorage.getItem("authUser");
      if (!authUser) return "Usuario";
      const obj = JSON.parse(authUser);
      return (
        obj?.usuario?.nombreUsuario ||
        obj?.username ||
        obj?.email ||
        "Usuario"
      );
    } catch {
      return "Usuario";
    }
  };

  const userName = useMemo(() => getUserName(), []);
  const userInitial = useMemo(
    () => userName.trim().charAt(0).toUpperCase() || "U",
    [userName],
  );
  const avatarColors = [
    "#1f77b4",
    "#e67e22",
    "#8e44ad",
    "#16a085",
    "#e74c3c",
    "#2d6a4f",
    "#a15c5c",
  ];
  const userAvatarColor = useMemo(() => {
    const hash = Array.from(userName as string).reduce<number>(
      (acc, char) => acc + String(char).charCodeAt(0),
      0,
    );
    return avatarColors[Math.abs(hash) % avatarColors.length];
  }, [userName]);

  const formatDateTime = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("es-PY", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(date);
  };

  const conversationItems = useMemo(
    () =>
      messages.map((item) => {
        const isMe = item.rol === "usuario";
        const rawText = String(item.contenido ?? "");
        const cleanedText = rawText
          .replace(/^\s*Proveedor:\s*.*$/gim, "")
          .trim();
        const providerLabel = null;
        const renderContent = () => {
          const lines = cleanedText
            .split("\n")
            .filter((line) => line.trim().length > 0);
          const blocks: React.ReactNode[] = [];
          let listBuffer: string[] = [];
          let listType: "ul" | "ol" | null = null;

          const flushList = () => {
            if (listBuffer.length === 0 || !listType) return;
            const items = listBuffer.map((entry, idx) => (
              <li key={`${listType}-${idx}`}>{entry}</li>
            ));
            blocks.push(
              listType === "ul" ? (
                <ul key={`ul-${blocks.length}`} className="agent-list">
                  {items}
                </ul>
              ) : (
                <ol key={`ol-${blocks.length}`} className="agent-list">
                  {items}
                </ol>
              ),
            );
            listBuffer = [];
            listType = null;
          };

          lines.forEach((line) => {
            const trimmed = line.trim();
            if (trimmed.startsWith("### ")) {
              flushList();
              blocks.push(
                <div key={`h3-${blocks.length}`} className="agent-heading agent-heading-sm">
                  {trimmed.slice(4)}
                </div>,
              );
              return;
            }
            if (trimmed.startsWith("## ")) {
              flushList();
              blocks.push(
                <div key={`h2-${blocks.length}`} className="agent-heading">
                  {trimmed.slice(3)}
                </div>,
              );
              return;
            }
            if (trimmed.startsWith("# ")) {
              flushList();
              blocks.push(
                <div key={`h1-${blocks.length}`} className="agent-heading agent-heading-lg">
                  {trimmed.slice(2)}
                </div>,
              );
              return;
            }
            if (/^\d+\.\s+/.test(trimmed)) {
              const value = trimmed.replace(/^\d+\.\s+/, "");
              if (listType && listType !== "ol") {
                flushList();
              }
              listType = "ol";
              listBuffer.push(value);
              return;
            }
            if (trimmed.startsWith("- ")) {
              const value = trimmed.slice(2);
              if (listType && listType !== "ul") {
                flushList();
              }
              listType = "ul";
              listBuffer.push(value);
              return;
            }
            flushList();
            blocks.push(
              <p key={`p-${blocks.length}`} className="agent-paragraph">
                {trimmed}
              </p>,
            );
          });
          flushList();
          return blocks;
        };
        const renderUiBlocks = () => {
          const blocks = uiBlocksByMessageId[item.id] ?? [];
          if (blocks.length === 0) return null;
          return (
            <div className="agent-ui-blocks">
              {blocks.map((block, index) => {
                if (block.type === "notice") {
                  return (
                    <div
                      key={`notice-${item.id}-${index}`}
                      className={`agent-notice agent-notice-${block.tone || "info"}`}
                    >
                      {block.text}
                    </div>
                  );
                }
                if (block.type === "kpis") {
                  return (
                    <div key={`kpi-${item.id}-${index}`} className="agent-kpi-grid">
                      {block.items.map((kpi, idx) => (
                        <div
                          key={`kpi-item-${idx}`}
                          className={`agent-kpi-card ${kpi.tone ? `agent-kpi-${kpi.tone}` : ""}`}
                        >
                          <div className="agent-kpi-label">{kpi.label}</div>
                          <div className="agent-kpi-value">{kpi.value}</div>
                        </div>
                      ))}
                    </div>
                  );
                }
                if (block.type === "list") {
                  return (
                    <div key={`list-${item.id}-${index}`} className="agent-list-block">
                      {block.title && (
                        <div className="agent-list-title">{block.title}</div>
                      )}
                      <ul className="agent-list">
                        {block.items.map((entry, idx) => (
                          <li key={`list-item-${idx}`}>{entry}</li>
                        ))}
                      </ul>
                    </div>
                  );
                }
                if (block.type === "table") {
                  return (
                    <div key={`table-${item.id}-${index}`} className="agent-table-block">
                      {block.title && (
                        <div className="agent-table-title">{block.title}</div>
                      )}
                      <Table size="sm" responsive className="agent-table">
                        <thead>
                          <tr>
                            {block.columns.map((col) => (
                              <th key={col}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {block.rows.map((row, rowIdx) => (
                            <tr key={`row-${rowIdx}`}>
                              {block.columns.map((col) => (
                                <td key={`${rowIdx}-${col}`}>
                                  {String(row?.[col] ?? "")}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        };
        return (
          <li key={item.id} className={isMe ? "right" : ""}>
            <div className="conversation-list">
              <div className="chat-avatar">
                <div className="avatar-xxs">
                  {isMe ? (
                    <span
                      className="avatar-title rounded-circle"
                      style={{ background: userAvatarColor }}
                    >
                      {userInitial}
                    </span>
                  ) : (
                    <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                      <i className="ri-robot-2-line"></i>
                    </span>
                  )}
                </div>
              </div>
              <div className="user-chat-content">
                <div className="ctext-wrap">
                  <div className="ctext-wrap-content">
                    <div className="agent-message">{renderContent()}</div>
                    {renderUiBlocks()}
                    <p className="chat-time mb-0">
                      <i className="ri-time-line align-middle"></i>{" "}
                      <span className="align-middle">{formatDateTime(item.creado_en)}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </li>
        );
      }),
    [messages, uiBlocksByMessageId],
  );

  const [pendingAutoAnalysis, setPendingAutoAnalysis] = useState(false);
  const compareInProgressRef = useRef(false);
  const processingTimeoutRef = useRef<number | null>(null);
  const [multiBranchFlow, setMultiBranchFlow] = useState<{
    branches: string[];
    index: number;
    snapshots: Array<{
      sucursal: string;
      filtros: any;
      datos: any;
    }>;
    fecha?: string | null;
    dateMode?: string | null;
    filters?: Record<string, any>;
  } | null>(null);
  const [multiDateFlow, setMultiDateFlow] = useState<{
    dates: string[];
    index: number;
    snapshots: Array<{
      fecha: string;
      filtros: any;
      datos: any;
    }>;
    filters?: Record<string, any>;
  } | null>(null);

  const normalizeBranchKeyLocal = (value?: string | null) => {
    if (!value) return "";
    return value
      .toString()
      .trim()
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Z0-9]/g, "");
  };

  const stripEmojis = (value: string) =>
    value.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, "");

  const extractActions = (text: string) => {
    let cleaned = text;
    const collected: string[] = [];
    cleaned = cleaned.replace(/```(?:json)?\s*([\s\S]*?)```/gi, (_full, inner) => {
      if (typeof inner === "string" && inner.includes("\"acciones\"")) {
        collected.push(inner.trim());
      }
      return "";
    });
    cleaned = cleaned.replace(/\{[\s\S]*?"acciones"[\s\S]*?\}/g, (match) => {
      collected.push(match.trim());
      return "";
    });
    cleaned = cleaned.replace(/```[\s\S]*?```/g, "");
    cleaned = stripEmojis(cleaned).replace(/\n{3,}/g, "\n\n").trim();
    const rawJson = collected.find((item) => item.includes("\"acciones\"")) ?? null;
    if (!rawJson) {
      return { cleaned, actions: null };
    }
    try {
      const parsed = JSON.parse(rawJson);
      return { cleaned, actions: parsed?.acciones ?? null };
    } catch {
      return { cleaned, actions: null };
    }
  };

  const buildUiBlockFromAction = (action: any): UiBlock | null => {
    const tipo = String(action?.tipo || "");
    if (tipo === "render_notice") {
      const text = String(action?.texto || action?.valor || "").trim();
      if (!text) return null;
      return { type: "notice", tone: action?.tone || "info", text, source: "agent" };
    }
    if (tipo === "render_kpis") {
      const items = Array.isArray(action?.items) ? action.items : null;
      if (!items || items.length === 0) {
        const resumen = snapshotPayload?.datos?.resumen;
        if (!resumen) return null;
        return {
          type: "kpis",
          items: [
            {
              label: "Artículos",
              value: String(resumen.totalArticulos ?? 0),
            },
            {
              label: "Diferencia total",
              value: String(resumen.totalDiferencia ?? 0),
            },
          ],
          source: "agent",
        };
      }
      return {
        type: "kpis",
        items: items.map((item: any) => ({
          label: String(item?.label ?? item?.titulo ?? ""),
          value: String(item?.value ?? item?.valor ?? ""),
          tone: item?.tone,
        })),
        source: "agent",
      };
    }
    if (tipo === "render_lista") {
      const items = Array.isArray(action?.items)
        ? action.items.map((item: any) => String(item))
        : [];
      if (items.length === 0) return null;
      return { type: "list", title: action?.title, items, source: "agent" };
    }
    if (tipo === "render_tabla_compacta" || tipo === "render_table") {
      const explicitRows = Array.isArray(action?.rows) ? action.rows : null;
      const actionSource =
        typeof action?.source === "string" && action.source.trim().length > 0
          ? action.source.trim()
          : null;
      if (explicitRows && explicitRows.length > 0) {
        const columns = Array.isArray(action?.columns) ? action.columns : Object.keys(explicitRows[0] ?? {});
        if (columns.length === 0) return null;
        return {
          type: "table",
          title: action?.title,
          columns,
          rows: explicitRows.slice(0, Math.min(Number(action?.limit ?? 10), 10)),
          source: actionSource === "summary" ? "summary" : "agent",
        };
      }
      const source = actionSource || action?.tabla || "stockPorArticulo";
      const tableData = snapshotPayload?.datos?.tablas?.[source];
      if (!tableData || !Array.isArray(tableData.rows)) return null;
      const limit = Math.min(Number(action?.limit ?? 10), 10);
      const defaultColumnsMap: Record<string, string[]> = {
        stockPorArticulo: ["articulo", "diferencia"],
        movimientosPorFecha: ["articulo", "totalCantidad", "totalMonto"],
        familiaLinea: ["familia", "linea", "ventas", "diferencia"],
        sucursal: ["sucursal", "ventas", "diferencia"],
        multiSucursalResumen: ["sucursal", "totalDiferencia", "totalArticulos", "topNegativo"],
        multiFechaResumen: ["fecha", "totalDiferencia", "totalArticulos", "topNegativo"],
      };
      const columns = Array.isArray(action?.columns)
        ? action.columns
        : defaultColumnsMap[source] ?? [];
      if (columns.length === 0) return null;
      const rows = tableData.rows.slice(0, limit).map((row: any) => row);
      return {
        type: "table",
        title: action?.title,
        columns,
        rows,
        source: "report",
      };
    }
    return null;
  };

  const appendAgentMessage = (agentMessage: ChatMessage) => {
    setMessages((prev) => [...prev, agentMessage]);
    if (agentMessage.rol !== "agente") {
      pendingUiBlocksRef.current = null;
      return;
    }
    const pending = pendingUiBlocksRef.current;
    if (pending && pending.length > 0) {
      const first = pending[0];
      if (first?.source !== "summary") {
        setUiBlocksByMessageId((prev) => ({
          ...prev,
          [agentMessage.id]: pending.slice(0, 1),
        }));
      }
      if (first?.type === "table" && (first?.source === "agent" || first?.source === "summary")) {
        onApplyUiAction?.({
          tipo: "agente_tabla_resumen",
          title: first.title,
          columns: first.columns,
          rows: first.rows,
        });
      }
      pendingUiBlocksRef.current = null;
    }
  };

  const appendSummaryHintIfNeeded = (text: string) => {
    const pending = pendingUiBlocksRef.current;
    if (
      pending &&
      pending.length > 0 &&
      pending[0]?.type === "table" &&
      (pending[0]?.source === "agent" || pending[0]?.source === "summary")
    ) {
      return `${text}\n\nResultado cargado en pestaña Resumen.`;
    }
    return text;
  };

  const compactSnapshot = (input: any) => {
    if (!input || typeof input !== "object") return input;
    const resumen = input?.resumen ?? null;
    const tablas = input?.tablas ?? {};
    const compactTablas: any = {};
    Object.entries(tablas).forEach(([key, value]: any) => {
      if (!value || typeof value !== "object") return;
      const rows = Array.isArray(value.rows) ? value.rows.slice(0, 20) : [];
      compactTablas[key] = { rows, limit: rows.length };
    });
    return {
      meta: input?.meta ?? null,
      resumen,
      tarjetasResumen: input?.tarjetasResumen ?? null,
      tablas: compactTablas,
      detallesMovimientos: input?.detallesMovimientos
        ? {
            metric: input.detallesMovimientos.metric,
            rows: Array.isArray(input.detallesMovimientos.rows)
              ? input.detallesMovimientos.rows.slice(0, 20)
              : [],
            limit: 20,
            total: input.detallesMovimientos.total ?? 0,
          }
        : null,
      ui: input?.ui ?? null,
    };
  };

  const normalizeShortDate = (value: any) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return trimmed;
    const fullMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (fullMatch) {
      const day = String(Math.min(31, Math.max(1, Number(fullMatch[1])))).padStart(2, "0");
      const month = String(Math.min(12, Math.max(1, Number(fullMatch[2])))).padStart(2, "0");
      const year = fullMatch[3];
      return `${year}-${month}-${day}`;
    }
    const shortMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})$/);
    if (!shortMatch) return value;
    const year = new Date().getFullYear();
    const month = String(Math.min(12, Math.max(1, Number(shortMatch[2])))).padStart(2, "0");
    const day = String(Math.min(31, Math.max(1, Number(shortMatch[1])))).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const applyActions = (actions: any) => {
    if (!Array.isArray(actions)) return;
    const payload: any = {};
    const uiActions: any[] = [];
    let shouldAnalyzeAfterFilters = false;
    let hasCompareSucursales = false;
    let hasCompareFechas = false;
    let compareSucursales: string[] = [];
    let compareFecha: string | null = null;
    let compareModoFecha: string | null = null;
    actions.forEach((action) => {
      if (action?.tipo === "cambiar_pestana" && action?.valor) {
        onApplyTab?.(action.valor);
      }
      if (action?.tipo === "abrir_movimientos") {
        onApplyUiAction?.({ tipo: "ver_movimientos" });
      }
      if (action?.tipo === "comparar_sucursales") {
        const sucursales = Array.isArray(action?.sucursales)
          ? action.sucursales.map((s: any) => String(s).trim()).filter(Boolean)
          : [];
        if (sucursales.length > 0) {
          hasCompareSucursales = true;
          compareSucursales = sucursales;
          if (payload.sucursal === undefined) {
            payload.sucursal = sucursales;
          }
          compareFecha = action?.fecha || payload?.fecha || null;
          compareModoFecha = action?.modo_fecha || action?.dateMode || "day";
        }
      }
      if (action?.tipo === "comparar_fechas") {
        const fechas = Array.isArray(action?.fechas)
          ? action.fechas.map((f: any) => String(f).trim()).filter(Boolean)
          : [];
        if (fechas.length > 0) {
          hasCompareFechas = true;
        }
      }
      if (action?.tipo === "set_filtro") {
        const campo = action?.campo;
        const valor = action?.valor;
        if (campo === "fecha") payload.fecha = normalizeShortDate(valor);
        if (campo === "sucursal" || campo === "sucursales") payload.sucursal = valor;
        if (campo === "familia") payload.familia = valor;
        if (campo === "linea") payload.linea = valor;
        if (campo === "servidor") payload.servidor = valor;
        if (campo === "buscar") payload.searchTerm = valor;
        if (campo === "semantico") payload.semanticTokens = valor;
        if (campo === "coleccion") payload.collectionFilter = valor;
        if (campo === "idmov_inicial") payload.inventoryMovementInitial = valor;
        if (campo === "idmov_final") payload.inventoryMovementFinal = valor;
        if (campo === "vista") payload.groupViewMode = valor;
        if (campo === "diferencia") payload.differenceFilter = valor;
        if (campo === "modo_control") payload.controlMode = valor;
        if (campo === "modo_metricas") payload.metricMode = valor;
        if (campo === "modo_fecha") payload.dateMode = valor;
        if (campo === "semana") payload.week = valor;
        if (campo === "mes") payload.month = valor;
        if (campo === "rango_desde") payload.rangeFrom = valor;
        if (campo === "rango_hasta") payload.rangeTo = valor;
        shouldAnalyzeAfterFilters = true;
      }
      if (action?.tipo === "set_filtro_semantico") {
        const tokens = Array.isArray(action?.tokens)
          ? action.tokens.map((t: any) => String(t).trim()).filter(Boolean)
          : [];
        if (tokens.length > 0) {
          payload.semanticTokens = tokens;
          shouldAnalyzeAfterFilters = true;
        }
      }
      if (action?.tipo === "set_fecha") {
        payload.dateMode = action?.modo ?? payload.dateMode;
        if (action?.dia) payload.fecha = normalizeShortDate(action.dia);
        if (action?.semana) payload.week = action.semana;
        if (action?.mes) payload.month = action.mes;
        if (action?.desde) payload.rangeFrom = action.desde;
        if (action?.hasta) payload.rangeTo = action.hasta;
        shouldAnalyzeAfterFilters = true;
      }
      if (
        action?.tipo === "descargar_excel" ||
        action?.tipo === "descargar_pdf" ||
        action?.tipo === "pdf_ocultar_columna" ||
        String(action?.tipo || "").startsWith("coleccion_")
      ) {
        uiActions.push(action);
      }
      if (action?.tipo === "descargar_excel" || action?.tipo === "descargar_pdf") {
        // Sin mensajes intermedios: usar solo indicador de escritura.
      }
      if (String(action?.tipo || "").startsWith("render_")) {
        const block = buildUiBlockFromAction(action);
        if (block) {
          uiActions.push(action);
          pendingUiBlocksRef.current = [
            ...(pendingUiBlocksRef.current ?? []),
            block,
          ];
        }
      }
    });
    if (Object.keys(payload).length > 0) {
      if (payload.dateMode === "day") {
        delete payload.rangeFrom;
        delete payload.rangeTo;
      }
      if (payload.dateMode === "range") {
        delete payload.fecha;
      }
      if ((payload.rangeFrom || payload.rangeTo) && !payload.dateMode) {
        payload.dateMode = "range";
        delete payload.fecha;
      }
      if (payload.fecha) {
        payload.fecha = normalizeShortDate(payload.fecha);
      }
      if (payload.rangeFrom) payload.rangeFrom = normalizeShortDate(payload.rangeFrom);
      if (payload.rangeTo) payload.rangeTo = normalizeShortDate(payload.rangeTo);
      if (
        payload.dateMode === "range" &&
        payload.rangeFrom &&
        payload.rangeTo &&
        payload.rangeFrom === payload.rangeTo
      ) {
        payload.dateMode = "day";
        payload.fecha = payload.rangeFrom;
        delete payload.rangeFrom;
        delete payload.rangeTo;
      }
      const suc = payload.sucursal;
      if (Array.isArray(suc) && suc.length === 1) {
        payload.sucursal = String(suc[0] ?? "").trim();
      }
    }
    if (hasCompareSucursales && compareSucursales.length > 0) {
      if (multiBranchFlow) {
        return;
      }
      compareInProgressRef.current = true;
      if (compareFecha) compareFecha = normalizeShortDate(compareFecha);
      setMultiBranchFlow({
        branches: compareSucursales,
        index: 0,
        snapshots: [],
        fecha: compareFecha,
        dateMode: compareModoFecha || "day",
        filters: { ...payload, sucursal: undefined },
      });
      setPendingAutoAnalysis(false);
      onApplyFilters?.({
        ...payload,
        fecha: compareFecha,
        dateMode: compareModoFecha || payload.dateMode || "day",
        sucursal: compareSucursales[0],
      });
    } else if (hasCompareFechas) {
      const fechas = Array.isArray(actions.find((a: any) => a?.tipo === "comparar_fechas")?.fechas)
        ? actions.find((a: any) => a?.tipo === "comparar_fechas")?.fechas.map((f: any) => String(f).trim()).filter(Boolean)
        : [];
      if (fechas.length > 0) {
        if (multiDateFlow) {
          return;
        }
        compareInProgressRef.current = true;
        setMultiDateFlow({
          dates: fechas,
          index: 0,
          snapshots: [],
          filters: { ...payload },
        });
        setPendingAutoAnalysis(false);
        onApplyFilters?.({ ...payload, fecha: fechas[0], dateMode: "day" });
      }
    } else if (Object.keys(payload).length > 0) {
      onApplyFilters?.(payload);
      if (shouldAnalyzeAfterFilters) {
        setPendingAutoAnalysis(true);
      }
    }
    uiActions.forEach(async (action) => {
      if (String(action?.tipo || "").startsWith("render_")) {
        return;
      }
      if (String(action?.tipo || "").startsWith("coleccion_")) {
        const canEdit = collectionPermissions?.canEdit ?? false;
        const canClone = collectionPermissions?.canClone ?? false;
        const canView = collectionPermissions?.canView ?? false;
        const needsEdit = [
          "coleccion_crear",
          "coleccion_editar",
          "coleccion_eliminar",
          "coleccion_orden",
          "coleccion_asignar_articulo",
          "coleccion_quitar_articulo",
        ].includes(action.tipo);
        const needsClone = [
          "coleccion_clonar",
          "coleccion_clonar_usuario",
        ].includes(action.tipo);
        if ((needsEdit && !canEdit) || (needsClone && !canClone) || (!canView && !needsEdit && !needsClone)) {
          if (activeSessionId) {
            const aviso = await requestJson<ChatMessage>(
              `/api/informes/agente/sesiones/${activeSessionId}/mensajes`,
              {
                method: "POST",
                body: JSON.stringify({
                  rol: "agente",
                  contenido:
                    "No tenés permisos para realizar esa acción de colecciones.",
                }),
              },
            );
            if (aviso) {
              appendAgentMessage(aviso);
            }
          }
          return;
        }
      }
      await onApplyUiAction?.(action);
    });
  };

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed || !activeSessionId) {
      return;
    }
    const optimisticMessage: ChatMessage = {
      id: Date.now(),
      rol: "usuario",
      contenido: trimmed,
      creado_en: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticMessage]);
    setMessage("");
    const enviar = async () => {
      setIsTyping(true);
      setProcessingState("Enviando consulta");
      const saved = await requestJson<ChatMessage>(
        `/api/informes/agente/sesiones/${activeSessionId}/mensajes`,
        {
          method: "POST",
          body: JSON.stringify({ rol: "usuario", contenido: trimmed }),
        },
      );
      if (saved) {
        setMessages((prev) =>
          prev.map((item) => (item.id === optimisticMessage.id ? saved : item)),
        );
        setSessions((prev) =>
          prev.map((item) =>
            item.id === activeSessionId
              ? { ...item, preview: saved.contenido, actualizado_en: saved.creado_en }
              : item,
          ),
        );
        lastUserMessageIdRef.current = saved.id;
      }
      if (snapshotPayload) {
        setProcessingState("Guardando snapshot");
        await requestJson(`/api/informes/agente/sesiones/${activeSessionId}/snapshots`, {
          method: "POST",
          body: JSON.stringify({
            informeKey,
            filtros: snapshotPayload.filtros,
            datos: compactSnapshot(snapshotPayload.datos),
          }),
        });
      }
      if (!snapshotReady) {
        setProcessingState("Esperando datos");
        setPendingAutoAnalysis(true);
        return;
      }
      if (snapshotPayload?.datos?.meta?.cargando) {
        setProcessingState("Cargando datos del informe");
        setPendingAutoAnalysis(true);
        return;
      }
      if (isAnalyzingRef.current) return;
      isAnalyzingRef.current = true;
      setProcessingState("Analizando");
      const analisis = await requestJson<{
        mensaje: string;
        acciones?: any[];
        proveedor?: string;
      }>(
        `/api/informes/agente/sesiones/${activeSessionId}/analizar`,
        {
          method: "POST",
          body: JSON.stringify({ compararAnterior: false }),
        },
      );
      if (analisis?.mensaje) {
        let textoFinal = analisis.mensaje;
        let acciones = analisis.acciones ?? null;
        if (!acciones || acciones.length === 0) {
          const parsed = extractActions(analisis.mensaje);
          textoFinal = parsed.cleaned;
          acciones = parsed.actions ?? null;
        }
        const hasCompareAction =
          Array.isArray(acciones) &&
          acciones.some((action: any) =>
            ["comparar_sucursales", "comparar_fechas"].includes(
              String(action?.tipo || ""),
            ),
          );
        if (acciones) {
          applyActions(acciones);
        }
        const needsReload =
          Array.isArray(acciones) &&
          acciones.some((action: any) =>
            [
              "set_filtro",
              "set_filtro_semantico",
              "comparar_sucursales",
              "comparar_fechas",
              "cambiar_pestana",
              "abrir_movimientos",
              "descargar_excel",
              "descargar_pdf",
              "pdf_ocultar_columna",
            ].includes(String(action?.tipo || "")),
          );
        if (needsReload) {
          setProcessingState("Cargando datos");
          setPendingAutoAnalysis(true);
          isAnalyzingRef.current = false;
          setIsTyping(true);
          return;
        }
        if (
          !pendingUiBlocksRef.current &&
          snapshotPayload?.datos?.tablas?.multiSucursalResumen?.rows?.length
        ) {
          pendingUiBlocksRef.current = [
            {
              type: "table",
              title: "Comparación por sucursal",
              columns: ["sucursal", "totalDiferencia", "totalArticulos", "topNegativo"],
              rows: snapshotPayload.datos.tablas.multiSucursalResumen.rows,
            },
          ];
        }
        if (!hasCompareAction) {
          textoFinal = appendSummaryHintIfNeeded(textoFinal);
          const lastAgent = [...messages].reverse().find((item) => item.rol === "agente");
          if (!lastAgent || lastAgent.contenido !== textoFinal) {
            const agentMessage = await requestJson<ChatMessage>(
              `/api/informes/agente/sesiones/${activeSessionId}/mensajes`,
              {
                method: "POST",
                body: JSON.stringify({ rol: "agente", contenido: textoFinal }),
              },
            );
            if (agentMessage) {
              appendAgentMessage(agentMessage);
              if (lastUserMessageIdRef.current) {
                lastAnalyzedMessageIdRef.current = lastUserMessageIdRef.current;
              }
            }
          }
        } else {
          setProcessingState("Comparando sucursales");
        }
      }
      isAnalyzingRef.current = false;
      setIsTyping(false);
      setProcessingState(null);
    };
    enviar().catch((err) => {
      setError(err?.message || "No se pudo enviar el mensaje.");
      setIsTyping(false);
      setProcessingState(null);
    });
  };

  useEffect(() => {
    if (compareInProgressRef.current) return;
    if (multiBranchFlow || multiDateFlow || !pendingAutoAnalysis) return;
    if (
      lastUserMessageIdRef.current &&
      lastAnalyzedMessageIdRef.current === lastUserMessageIdRef.current
    ) {
      return;
    }
    if (!activeSessionId || !snapshotReady) return;
    if (snapshotPayload?.datos?.meta?.cargando) return;
    const run = async () => {
      setIsTyping(true);
      setProcessingState("Analizando");
      if (isAnalyzingRef.current) return;
      isAnalyzingRef.current = true;
      if (snapshotPayload) {
        await requestJson(`/api/informes/agente/sesiones/${activeSessionId}/snapshots`, {
          method: "POST",
          body: JSON.stringify({
            informeKey,
            filtros: snapshotPayload.filtros,
            datos: compactSnapshot(snapshotPayload.datos),
          }),
        });
      }
      const analisis = await requestJson<{
        mensaje: string;
        acciones?: any[];
        proveedor?: string;
      }>(
        `/api/informes/agente/sesiones/${activeSessionId}/analizar`,
        {
          method: "POST",
          body: JSON.stringify({ compararAnterior: false }),
        },
      );
        if (analisis?.mensaje) {
          let textoFinal = analisis.mensaje;
          let acciones = analisis.acciones ?? null;
          if (!acciones || acciones.length === 0) {
            const parsed = extractActions(analisis.mensaje);
            textoFinal = parsed.cleaned;
            acciones = parsed.actions ?? null;
          }
          if (acciones) {
            applyActions(acciones);
          }
          if (
            !pendingUiBlocksRef.current &&
            snapshotPayload?.datos?.tablas?.multiSucursalResumen?.rows?.length
          ) {
            pendingUiBlocksRef.current = [
              {
                type: "table",
                title: "Comparación por sucursal",
                columns: ["sucursal", "totalDiferencia", "totalArticulos", "topNegativo"],
                rows: snapshotPayload.datos.tablas.multiSucursalResumen.rows,
              },
            ];
          }
          textoFinal = appendSummaryHintIfNeeded(textoFinal);
          const lastAgent = [...messages].reverse().find((item) => item.rol === "agente");
          if (!lastAgent || lastAgent.contenido !== textoFinal) {
            const agentMessage = await requestJson<ChatMessage>(
              `/api/informes/agente/sesiones/${activeSessionId}/mensajes`,
              {
              method: "POST",
              body: JSON.stringify({ rol: "agente", contenido: textoFinal }),
            },
          );
          if (agentMessage) {
            appendAgentMessage(agentMessage);
            if (lastUserMessageIdRef.current) {
              lastAnalyzedMessageIdRef.current = lastUserMessageIdRef.current;
            }
          }
        }
      }
      setIsTyping(false);
      isAnalyzingRef.current = false;
      setPendingAutoAnalysis(false);
      setProcessingState(null);
    };
    run().catch((err) => {
      setError(err?.message || "No se pudo analizar.");
      setIsTyping(false);
      setProcessingState(null);
    });
  }, [
    pendingAutoAnalysis,
    snapshotPayload,
    activeSessionId,
    snapshotReady,
    informeKey,
    multiBranchFlow,
    multiDateFlow,
  ]);

  useEffect(() => {
    if (!multiBranchFlow || !activeSessionId) return;
    if (!snapshotReady || !snapshotPayload) return;
    if (snapshotPayload?.datos?.meta?.cargando) return;
    if (pendingAutoAnalysis) return;

    const currentBranch = multiBranchFlow.branches[multiBranchFlow.index];
    if (!currentBranch) return;
    if (!Array.isArray(snapshotPayload.filtros?.sucursal)) return;
    const currentDay = snapshotPayload?.filtros?.dateSelection?.day || null;
    if (multiBranchFlow.fecha && currentDay !== multiBranchFlow.fecha) {
      return;
    }
    const currentKey = normalizeBranchKeyLocal(currentBranch);
    const hasBranch = snapshotPayload.filtros.sucursal.some(
      (item: string) => normalizeBranchKeyLocal(item) === currentKey,
    );
    if (!hasBranch) return;

    const run = async () => {
      setProcessingState(
        `Procesando sucursal ${multiBranchFlow.index + 1} de ${multiBranchFlow.branches.length}`,
      );
      const updatedSnapshots = [
        ...multiBranchFlow.snapshots,
        {
          sucursal: currentBranch,
          filtros: snapshotPayload.filtros,
          datos: compactSnapshot(snapshotPayload.datos),
        },
      ];
      if (multiBranchFlow.index + 1 < multiBranchFlow.branches.length) {
        const nextBranch = multiBranchFlow.branches[multiBranchFlow.index + 1];
        setMultiBranchFlow({
          branches: multiBranchFlow.branches,
          index: multiBranchFlow.index + 1,
          snapshots: updatedSnapshots,
          fecha: multiBranchFlow.fecha,
          dateMode: multiBranchFlow.dateMode,
        });
        debugLog("multiBranch:next", { fecha: snapshotPayload?.filtros?.dateSelection?.day || null, sucursal: nextBranch });
        onApplyFilters?.({
          fecha: multiBranchFlow.fecha || snapshotPayload?.filtros?.dateSelection?.day || null,
          dateMode: multiBranchFlow.dateMode || "day",
          sucursal: nextBranch,
        });
        return;
      }

      setIsTyping(true);
      setProcessingState("Analizando comparación");
      const multiSucursalResumen = updatedSnapshots.map((item) => {
        const resumen = item?.datos?.resumen ?? {};
        const topNegativo =
          Array.isArray(resumen.topNegativos) && resumen.topNegativos.length > 0
            ? resumen.topNegativos[0]?.articulo ?? ""
            : "";
        return {
          sucursal: item.sucursal,
          totalDiferencia: resumen.totalDiferencia ?? 0,
          totalArticulos: resumen.totalArticulos ?? 0,
          topNegativo,
        };
      });
      const hasSearch =
        Boolean(snapshotPayload?.filtros?.searchTerm) ||
        (Array.isArray(snapshotPayload?.filtros?.semanticTokens) &&
          snapshotPayload.filtros.semanticTokens.length > 0);
      const multiSucursalDetalle = hasSearch
        ? updatedSnapshots.flatMap((item) => {
            const rows = item?.datos?.tablas?.stockPorArticulo?.rows ?? [];
            return rows.map((row: any) => ({
              sucursal: item.sucursal,
              articulo: row?.articulo ?? "",
              diferencia: row?.diferencia ?? 0,
            }));
          })
        : [];
      if (hasSearch && multiSucursalDetalle.length > 0) {
        pendingUiBlocksRef.current = [
          {
            type: "table",
            title: "Diferencias por artículo y sucursal",
            columns: ["sucursal", "articulo", "diferencia"],
            rows: multiSucursalDetalle.slice(0, 15),
            source: "summary",
          },
        ];
      } else {
        pendingUiBlocksRef.current = [
          {
            type: "table",
            title: "Comparación por sucursal",
            columns: ["sucursal", "totalDiferencia", "totalArticulos", "topNegativo"],
            rows: multiSucursalResumen.slice(0, 15),
            source: "summary",
          },
        ];
      }

      await requestJson(`/api/informes/agente/sesiones/${activeSessionId}/snapshots`, {
        method: "POST",
        body: JSON.stringify({
          informeKey,
          filtros: { ...snapshotPayload.filtros, sucursal: multiBranchFlow.branches },
          datos: {
            ...compactSnapshot(snapshotPayload.datos),
            multiSucursal: updatedSnapshots,
            tablas: {
              ...(compactSnapshot(snapshotPayload.datos)?.tablas ?? {}),
              multiSucursalResumen: {
                rows: multiSucursalResumen,
              },
              ...(multiSucursalDetalle.length > 0
                ? {
                    multiSucursalDetalle: {
                      rows: multiSucursalDetalle,
                    },
                  }
                : {}),
            },
          },
        }),
      });
      const analisis = await requestJson<{
        mensaje: string;
        acciones?: any[];
        proveedor?: string;
      }>(`/api/informes/agente/sesiones/${activeSessionId}/analizar`, {
        method: "POST",
        body: JSON.stringify({ compararAnterior: false }),
      });
      if (analisis?.mensaje) {
        let textoFinal = analisis.mensaje;
        let acciones = analisis.acciones ?? null;
        if (!acciones || acciones.length === 0) {
          const parsed = extractActions(analisis.mensaje);
          textoFinal = parsed.cleaned;
          acciones = parsed.actions ?? null;
        }
        if (acciones) {
          applyActions(acciones);
        }
        if (
          !pendingUiBlocksRef.current &&
          snapshotPayload?.datos?.tablas?.multiSucursalResumen?.rows?.length
        ) {
          pendingUiBlocksRef.current = [
            {
              type: "table",
              title: "Comparación por sucursal",
              columns: ["sucursal", "totalDiferencia", "totalArticulos", "topNegativo"],
              rows: snapshotPayload.datos.tablas.multiSucursalResumen.rows,
            },
          ];
        }
        textoFinal = appendSummaryHintIfNeeded(textoFinal);
        const lastAgent = [...messages].reverse().find((item) => item.rol === "agente");
        if (!lastAgent || lastAgent.contenido !== textoFinal) {
          const agentMessage = await requestJson<ChatMessage>(
            `/api/informes/agente/sesiones/${activeSessionId}/mensajes`,
            {
              method: "POST",
              body: JSON.stringify({ rol: "agente", contenido: textoFinal }),
            },
          );
          if (agentMessage) {
            appendAgentMessage(agentMessage);
            if (lastUserMessageIdRef.current) {
              lastAnalyzedMessageIdRef.current = lastUserMessageIdRef.current;
            }
          }
        }
      }
      onApplyFilters?.({
        sucursal: multiBranchFlow.branches,
        fecha: multiBranchFlow.fecha || snapshotPayload?.filtros?.dateSelection?.day || null,
        dateMode: multiBranchFlow.dateMode || "day",
      });
      setIsTyping(false);
      setMultiBranchFlow(null);
      compareInProgressRef.current = false;
      setProcessingState(null);
    };

    run().catch((err) => {
      setError(err?.message || "No se pudo analizar por sucursal.");
      setIsTyping(false);
      setMultiBranchFlow(null);
      setProcessingState(null);
    });
  }, [
    multiBranchFlow,
    activeSessionId,
    snapshotReady,
    snapshotPayload,
    pendingAutoAnalysis,
    onApplyFilters,
    informeKey,
  ]);

  useEffect(() => {
    if (!multiDateFlow || !activeSessionId) return;
    if (!snapshotReady || !snapshotPayload) return;
    if (snapshotPayload?.datos?.meta?.cargando) return;
    if (pendingAutoAnalysis) return;

    const currentDate = multiDateFlow.dates[multiDateFlow.index];
    if (!currentDate) return;
    const currentDay = snapshotPayload?.filtros?.dateSelection?.day;
    if (currentDay !== currentDate) return;

    const run = async () => {
      setProcessingState(
        `Procesando fecha ${multiDateFlow.index + 1} de ${multiDateFlow.dates.length}`,
      );
      const updatedSnapshots = [
        ...multiDateFlow.snapshots,
        {
          fecha: currentDate,
          filtros: snapshotPayload.filtros,
          datos: compactSnapshot(snapshotPayload.datos),
        },
      ];
      if (multiDateFlow.index + 1 < multiDateFlow.dates.length) {
        const nextDate = multiDateFlow.dates[multiDateFlow.index + 1];
        setMultiDateFlow({
          dates: multiDateFlow.dates,
          index: multiDateFlow.index + 1,
          snapshots: updatedSnapshots,
        });
        debugLog("multiDate:next", { fecha: nextDate });
        onApplyFilters?.({ fecha: nextDate, dateMode: "day" });
        return;
      }

      setIsTyping(true);
      setProcessingState("Analizando comparación");
      const multiFechaResumen = updatedSnapshots.map((item) => {
        const resumen = item?.datos?.resumen ?? {};
        const topNegativo =
          Array.isArray(resumen.topNegativos) && resumen.topNegativos.length > 0
            ? resumen.topNegativos[0]?.articulo ?? ""
            : "";
        return {
          fecha: item.fecha,
          totalDiferencia: resumen.totalDiferencia ?? 0,
          totalArticulos: resumen.totalArticulos ?? 0,
          topNegativo,
        };
      });
      pendingUiBlocksRef.current = [
        {
          type: "table",
          title: "Comparación por fecha",
          columns: ["fecha", "totalDiferencia", "totalArticulos", "topNegativo"],
          rows: multiFechaResumen.slice(0, 15),
          source: "summary",
        },
      ];

      await requestJson(`/api/informes/agente/sesiones/${activeSessionId}/snapshots`, {
        method: "POST",
        body: JSON.stringify({
          informeKey,
          filtros: snapshotPayload.filtros,
          datos: {
            ...compactSnapshot(snapshotPayload.datos),
            multiFechas: updatedSnapshots,
            tablas: {
              ...(compactSnapshot(snapshotPayload.datos)?.tablas ?? {}),
              multiFechaResumen: {
                rows: multiFechaResumen,
              },
            },
          },
        }),
      });
      const analisis = await requestJson<{
        mensaje: string;
        acciones?: any[];
        proveedor?: string;
      }>(`/api/informes/agente/sesiones/${activeSessionId}/analizar`, {
        method: "POST",
        body: JSON.stringify({ compararAnterior: false }),
      });
      if (analisis?.mensaje) {
        let textoFinal = analisis.mensaje;
        let acciones = analisis.acciones ?? null;
        if (!acciones || acciones.length === 0) {
          const parsed = extractActions(analisis.mensaje);
          textoFinal = parsed.cleaned;
          acciones = parsed.actions ?? null;
        }
        if (acciones) {
          applyActions(acciones);
        }
        textoFinal = appendSummaryHintIfNeeded(textoFinal);
        const lastAgent = [...messages].reverse().find((item) => item.rol === "agente");
        if (!lastAgent || lastAgent.contenido !== textoFinal) {
          const agentMessage = await requestJson<ChatMessage>(
            `/api/informes/agente/sesiones/${activeSessionId}/mensajes`,
            {
              method: "POST",
              body: JSON.stringify({ rol: "agente", contenido: textoFinal }),
            },
          );
          if (agentMessage) {
            appendAgentMessage(agentMessage);
            if (lastUserMessageIdRef.current) {
              lastAnalyzedMessageIdRef.current = lastUserMessageIdRef.current;
            }
          }
        }
      }
      setIsTyping(false);
      setMultiDateFlow(null);
      compareInProgressRef.current = false;
      setProcessingState(null);
    };

    run().catch((err) => {
      setError(err?.message || "No se pudo comparar por fecha.");
      setIsTyping(false);
      setMultiDateFlow(null);
      setProcessingState(null);
    });
  }, [
    multiDateFlow,
    activeSessionId,
    snapshotReady,
    snapshotPayload,
    pendingAutoAnalysis,
    onApplyFilters,
    informeKey,
  ]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setError(null);
    setLoadingSessions(true);
    requestJson<ChatSession[]>(
      `/api/informes/agente/sesiones?informe=${encodeURIComponent(informeKey)}`,
    )
      .then((data) => {
        const sesiones = data ?? [];
        setSessions(sesiones);
        if (!activeSessionId && sesiones.length > 0) {
          setActiveSessionId(sesiones[0].id);
          return;
        }
        if (!activeSessionId && sesiones.length === 0) {
          requestJson<ChatSession>("/api/informes/agente/sesiones", {
            method: "POST",
            body: JSON.stringify({ informeKey }),
          })
            .then((nueva) => {
              if (nueva) {
                setSessions([nueva]);
                setActiveSessionId(nueva.id);
                setActiveTab("chat");
              }
            })
            .catch((err) => {
              setError(err?.message || "No se pudo crear el chat.");
            });
        }
      })
      .catch((err) => {
        setError(err?.message || "No se pudo cargar el historial.");
      })
      .finally(() => {
        setLoadingSessions(false);
      });
  }, [isOpen, informeKey, activeSessionId]);

  const createNewSession = async () => {
    try {
      const nueva = await requestJson<ChatSession>("/api/informes/agente/sesiones", {
        method: "POST",
        body: JSON.stringify({ informeKey }),
      });
      if (nueva) {
        setSessions((prev) => [nueva, ...prev]);
        setActiveSessionId(nueva.id);
      setMessages([]);
      lastUserMessageIdRef.current = null;
      lastAnalyzedMessageIdRef.current = null;
        setActiveTab("chat");
      }
    } catch (err: any) {
      setError(err?.message || "No se pudo crear un nuevo chat.");
    }
  };

  useEffect(() => {
    if (!isOpen || !activeSessionId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingMessages(true);
      try {
        const data = await requestJson<ChatMessage[]>(
          `/api/informes/agente/sesiones/${activeSessionId}/mensajes`,
        );
        if (cancelled) return;
        const list = data ?? [];
        setMessages(list);
        if (list.length === 0 && greetedSessionRef.current !== activeSessionId) {
          try {
            const saludoData = await requestJson<{ saludo?: string }>(
              `/api/informes/agente/saludo?informe=${encodeURIComponent(informeKey)}`,
            );
            const saludo = saludoData?.saludo?.trim();
            if (!cancelled && saludo) {
              setMessages((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  rol: "agente",
                  contenido: saludo,
                  creado_en: new Date().toISOString(),
                },
              ]);
              greetedSessionRef.current = activeSessionId;
            }
          } catch {
            // ignore greeting failures
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "No se pudo cargar los mensajes.");
        }
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeSessionId, informeKey]);

  useEffect(() => {
    if (isOpen) {
      greetedSessionRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (snapshotPayload?.datos?.meta?.cargando) {
      setProcessingState("Cargando datos del informe");
      return;
    }
    if (processingState === "Cargando datos del informe") {
      setProcessingState(null);
    }
  }, [snapshotPayload?.datos?.meta?.cargando, processingState]);

  useEffect(() => {
    if (processingTimeoutRef.current) {
      window.clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    if (!processingState) return;
    if (
      processingState === "Cargando datos" ||
      processingState === "Esperando datos" ||
      processingState === "Cargando datos del informe"
    ) {
      processingTimeoutRef.current = window.setTimeout(() => {
        setProcessingState(null);
        setIsTyping(false);
        setPendingAutoAnalysis(false);
      }, 25000);
    }
  }, [processingState]);

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={onToggle}
      direction="end"
      className="agent-offcanvas"
      style={{ width: 760 }}
    >
      <OffcanvasHeader toggle={onToggle}>
        <div className="d-flex align-items-center gap-2">
          <span className="avatar-xs rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center">
            <i className="ri-robot-2-line"></i>
          </span>
          <div>
            <div className="fw-semibold">Agente IA</div>
            <div className="text-muted small">Listo para analizar este informe</div>
          </div>
        </div>
      </OffcanvasHeader>
      <OffcanvasBody className="p-0">
        <div className="chat-wrapper h-100 d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between px-3 pt-3">
            <Nav tabs className="flex-grow-1">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "chat" })}
                onClick={() => setActiveTab("chat")}
              >
                Chat
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "history" })}
                onClick={() => setActiveTab("history")}
              >
                Historial
              </NavLink>
            </NavItem>
            </Nav>
            <Button color="primary" className="bg-gradient btn-sm ms-2" onClick={createNewSession}>
              <i className="ri-add-line me-1"></i>
              Nuevo chat
            </Button>
          </div>
          <TabContent activeTab={activeTab} className="flex-grow-1">
            <TabPane tabId="chat" className="h-100">
              <div className="user-chat h-100 d-flex flex-column">
                <div className="user-chat-topbar p-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="avatar-xs rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center">
                      <i className="ri-robot-2-line"></i>
                    </span>
                    <div>
                      <div className="fw-semibold">Agente IA</div>
                      <div className="text-muted small">Asistente de BaconApp</div>
                      {processingState && (
                        <div className="text-muted small">Estado: {processingState}</div>
                      )}
                    </div>
                  </div>
                </div>
              <div className="chat-conversation flex-grow-1">
                <SimpleBar className="h-100 p-3">
                  <style>{`
                    .typing-dots {
                      display: inline-flex;
                      gap: 6px;
                      align-items: center;
                    }
                    .typing-dots span {
                      width: 6px;
                      height: 6px;
                      background: #adb5bd;
                      border-radius: 50%;
                      display: inline-block;
                      animation: typingBounce 1.2s infinite ease-in-out;
                    }
                    .typing-dots span:nth-child(2) {
                      animation-delay: 0.2s;
                    }
                    .typing-dots span:nth-child(3) {
                      animation-delay: 0.4s;
                    }
                    @keyframes typingBounce {
                      0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
                      40% { transform: translateY(-6px); opacity: 1; }
                    }
                  `}</style>
                  <ul className="list-unstyled chat-conversation-list">
                      {conversationItems}
                      {isTyping && (
                        <li className="left">
                          <div className="conversation-list">
                            <div className="user-chat-content">
                              <div className="ctext-wrap">
                                <div className="ctext-wrap-content">
                                  <div className="agent-message">
                                    <span className="typing-dots">
                                      <span></span>
                                      <span></span>
                                      <span></span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      )}
                    </ul>
                  </SimpleBar>
                </div>
                <div className="chat-input-section p-3 border-top">
                  {error && (
                    <div className="alert alert-danger py-2 mb-2 small">
                      {error}
                    </div>
                  )}
                  <div className="d-flex align-items-center gap-2">
                    <Input
                      value={message}
                      placeholder="Escribí tu consulta..."
                      onChange={(event) => setMessage(event.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                    <Button
                      color="primary"
                      className="bg-gradient"
                      onClick={handleSend}
                      disabled={loadingMessages || !activeSessionId}
                    >
                      <i className="ri-send-plane-2-line"></i>
                    </Button>
                  </div>
                  <div className="text-muted small mt-2">
                    Agente de IA de BaconApp. Analiza el informe en lenguaje natural usando un LLM.
                  </div>
                </div>
              </div>
            </TabPane>
            <TabPane tabId="history" className="h-100">
              <div className="p-3 border-bottom">
                <div className="fw-semibold">Historial</div>
                <div className="text-muted small">Este informe</div>
              </div>
              <SimpleBar className="h-100">
                <ul className="list-unstyled chat-list m-0">
                  {loadingSessions && (
                    <li className="px-3 py-2 text-muted small">Cargando historial...</li>
                  )}
                  {!loadingSessions && sessions.length === 0 && (
                    <li className="px-3 py-2 text-muted small">Sin conversaciones aún.</li>
                  )}
                  {sessions.map((session) => (
                    <li key={session.id} className={session.id === activeSessionId ? "active" : ""}>
                      <button
                        type="button"
                        className="w-100 text-start border-0 bg-transparent"
                        onClick={() => {
                          setActiveSessionId(session.id);
                          setActiveTab("chat");
                        }}
                      >
                        <div className="w-100 text-truncate small">
                          <span className="text-muted">
                            {formatDateTime(session.actualizado_en)}
                          </span>{" "}
                          <span className="text-muted">-</span>{" "}
                          <span>{session.preview || session.titulo}</span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </SimpleBar>
            </TabPane>
          </TabContent>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default AgentWidget;
